/*
 * CGL photo field (Phase 4B, Custom Mini Canvas). See snippets/cgl-personalisation-set.liquid (field type
 * "image"), snippets/cgl-photo-dialog.liquid, docs/phase-4b-theme.md and docs/profile-schemas/mini-canvas@v4.json.
 *
 * - <cgl-photo> is one canvas's photo. The customer chooses a file; it goes to the CGL upload service with a
 *   progress bar. The service checks it, turns it the right way up, removes location and camera data and
 *   returns a private preview link that lasts minutes. The customer then positions, zooms and rotates the photo
 *   in the crop dialog, at the canvas's real print shape (from the service, never assumed square), checks the
 *   result and confirms it. Nothing is cropped without that confirmation and the uploaded photo is never changed.
 * - Rotation (mini-canvas@v4): any angle from -180 to 180 degrees in the spec's step (0.5), clockwise positive.
 *   The frame never shows a blank corner: turning the photo raises the smallest zoom to what that angle needs and
 *   keeps the customer's position as far as it can. The crop is a rectangle in the bounding box of the photo
 *   turned by the angle, exactly as the service checks it and as the print file is made from it.
 *   mini-canvas@v3 (a page loaded before v4) keeps quarter turns only.
 * - assets/cgl-personalisation.js reads each photo through status() and value() and puts the confirmed crop
 *   into _cgl_data. The readable cart property only says "Photo: Uploaded".
 * - The upload token (proof that this browser uploaded the photo) stays in this browser's localStorage, for
 *   cart thumbnails and editing. It never goes on the cart line or the order.
 * - <cgl-photo-thumb> draws a cart line's confirmed crop when this browser holds the token, and asks the service
 *   whether each photo still exists. When one has expired the line says so and checkout is held (see guard()).
 * - A refresh before Add to cart restores this tab's photos and crops (sessionStorage).
 */
(function () {
  if (window.CglPhoto) return;

  var TOKENS_KEY = 'cgl-photo-tokens';
  var SERVICE_KEY = 'cgl-photo-service';
  var TOKEN_DAYS = 30;
  var MM_PER_INCH = 25.4;
  var MIN_CROP_PX = 100; // zoom stops before the crop is smaller than this many photo pixels across
  var MAX_ZOOM = 10;
  var RULER_PX_PER_DEGREE = 8; // the rotation scale moves 8 CSS pixels per degree: 0.5 degree is 4 px by touch
  var CORNER_TOLERANCE_PX = 0.05; // the service's tolerance for a crop corner on the photo's edge
  var DEFAULT_ROTATION = { min: -180, max: 180, step: 0.5, snap: [0, 90, -90, 180], snap_within: 1, fine_step: 1 };

  // ---- small helpers ------------------------------------------------------------------------------------

  function storage(kind) {
    try {
      var s = window[kind];
      s.setItem('__cgl', '1');
      s.removeItem('__cgl');
      return s;
    } catch (e) {
      return null;
    }
  }
  var local = storage('localStorage');
  var tab = storage('sessionStorage');

  function readJson(store, key, fallback) {
    if (!store) return fallback;
    try {
      var raw = store.getItem(key);
      return raw ? JSON.parse(raw) : fallback;
    } catch (e) {
      return fallback;
    }
  }

  function writeJson(store, key, value) {
    if (!store) return;
    try {
      if (value === null) store.removeItem(key);
      else store.setItem(key, JSON.stringify(value));
    } catch (e) {
      /* full or blocked: the page still works, it just won't remember */
    }
  }

  function fill(template, values) {
    return String(template || '').replace(/\[(\w+)\]/g, function (match, name) {
      return Object.prototype.hasOwnProperty.call(values, name) ? values[name] : match;
    });
  }

  // Shopify's t filter HTML-escapes translations; these strings are set as text.
  function decodeStrings(strings) {
    var box = document.createElement('textarea');
    Object.keys(strings).forEach(function (key) {
      if (typeof strings[key] !== 'string') return;
      box.innerHTML = strings[key];
      strings[key] = box.value;
    });
    return strings;
  }

  var stringCache = null;
  function strings() {
    if (stringCache) return stringCache;
    var el = document.querySelector('script[data-cgl-photo-strings]');
    var parsed = {};
    try {
      parsed = el ? JSON.parse(el.textContent) : {};
    } catch (e) {
      parsed = {};
    }
    stringCache = decodeStrings(parsed);
    return stringCache;
  }

  function round6(n) {
    return Math.round(n * 1e6) / 1e6;
  }

  function clamp(n, lo, hi) {
    return Math.min(Math.max(n, lo), hi);
  }

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  // ---- rotation and crop geometry (the same maths as workers/uploads/src/lib/crop.ts) ------------------------

  // Sine and cosine of an angle in degrees, exact at quarter turns, built from + - * / only (the service's trig()).
  var RAD = Math.PI / 180;
  function sinSmall(x) {
    var x2 = x * x;
    return x * (1 - (x2 / 6) * (1 - (x2 / 20) * (1 - (x2 / 42) * (1 - (x2 / 72) * (1 - (x2 / 110) * (1 - (x2 / 156) * (1 - x2 / 210)))))));
  }
  function cosSmall(x) {
    var x2 = x * x;
    return 1 - (x2 / 2) * (1 - (x2 / 12) * (1 - (x2 / 30) * (1 - (x2 / 56) * (1 - (x2 / 90) * (1 - (x2 / 132) * (1 - (x2 / 182) * (1 - x2 / 240)))))));
  }
  function trig(degrees) {
    var a = degrees % 360;
    if (a < 0) a += 360;
    var quadrant = Math.floor(a / 90);
    var r = a - quadrant * 90;
    var s0;
    var c0;
    if (r === 0) {
      s0 = 0;
      c0 = 1;
    } else if (r <= 45) {
      s0 = sinSmall(r * RAD);
      c0 = cosSmall(r * RAD);
    } else {
      s0 = cosSmall((90 - r) * RAD);
      c0 = sinSmall((90 - r) * RAD);
    }
    if (quadrant === 0) return { c: c0, s: s0 };
    if (quadrant === 1) return { c: -s0 || 0, s: c0 };
    if (quadrant === 2) return { c: -c0, s: -s0 || 0 };
    return { c: s0, s: -c0 };
  }

  function isQuarter(angle) {
    return angle === 0 || angle === 90 || angle === 180 || angle === -90;
  }

  // Any number to the canonical angle: on the step grid, in (-180, 180], never -0.
  function wrapAngle(value, step) {
    step = step > 0 ? step : 0.5;
    var a = Math.round(value / step) * step;
    a = round6(((((a + 180) % 360) + 360) % 360) - 180);
    if (a <= -180) a += 360;
    return a === 0 ? 0 : a;
  }

  // mini-canvas@v3 quarter turns (0, 90, 180, 270 clockwise) and v4 angles.
  function fromV3(rotate) {
    return rotate === 270 ? -90 : rotate === 90 || rotate === 180 ? rotate : 0;
  }

  function toV3(angle) {
    return angle === -90 ? 270 : angle;
  }

  // The bounding box of a W x H photo turned by angle degrees.
  function turnedBox(width, height, angle) {
    if (angle === 0 || angle === 180) return { width: width, height: height };
    if (angle === 90 || angle === -90) return { width: height, height: width };
    var t = trig(angle);
    var ac = Math.abs(t.c);
    var as = Math.abs(t.s);
    return { width: width * ac + height * as, height: width * as + height * ac };
  }

  // A point in the turned box back to the upright photo, and the other way.
  function boxToMaster(X, Y, width, height, angle) {
    var box = turnedBox(width, height, angle);
    var t = trig(angle);
    var dx = X - box.width / 2;
    var dy = Y - box.height / 2;
    return { x: dx * t.c + dy * t.s + width / 2, y: -dx * t.s + dy * t.c + height / 2 };
  }

  function masterToBox(x, y, width, height, angle) {
    var box = turnedBox(width, height, angle);
    var t = trig(angle);
    var u = x - width / 2;
    var v = y - height / 2;
    return { x: box.width / 2 + t.c * u - t.s * v, y: box.height / 2 + t.s * u + t.c * v };
  }

  function cleanCrop(crop) {
    var w = clamp(round6(crop.w), 0.000001, 1);
    var h = clamp(round6(crop.h), 0.000001, 1);
    return { x: clamp(round6(crop.x), 0, round6(1 - w)), y: clamp(round6(crop.y), 0, round6(1 - h)), w: w, h: h };
  }

  // The service's cropPixels: whole pixels at a quarter turn.
  function cropPixels(crop, width, height) {
    var left = Math.round(crop.x * width);
    var top = Math.round(crop.y * height);
    var right = Math.round((crop.x + crop.w) * width);
    var bottom = Math.round((crop.y + crop.h) * height);
    left = clamp(left, 0, width - 1);
    top = clamp(top, 0, height - 1);
    right = clamp(right, left + 1, width);
    bottom = clamp(bottom, top + 1, height);
    return { width: right - left, height: bottom - top };
  }

  // The crop's size in photo pixels, as the service counts it for the print resolution.
  function cropSize(crop, width, height, angle) {
    var box = turnedBox(width, height, angle);
    if (isQuarter(angle)) return cropPixels(crop, Math.round(box.width), Math.round(box.height));
    return { width: crop.w * box.width, height: crop.h * box.height };
  }

  // True when every corner of the crop lands on the photo (no blank corner), as the service checks it.
  function cropFits(crop, width, height, angle) {
    if (crop.x < -1e-6 || crop.y < -1e-6 || crop.x + crop.w > 1 + 1e-6 || crop.y + crop.h > 1 + 1e-6) return false;
    if (isQuarter(angle)) return true;
    var box = turnedBox(width, height, angle);
    var l = crop.x * box.width;
    var t = crop.y * box.height;
    var r = l + crop.w * box.width;
    var b = t + crop.h * box.height;
    var corners = [
      [l, t],
      [r, t],
      [l, b],
      [r, b],
    ];
    for (var i = 0; i < 4; i += 1) {
      var p = boxToMaster(corners[i][0], corners[i][1], width, height, angle);
      if (p.x < -CORNER_TOLERANCE_PX || p.y < -CORNER_TOLERANCE_PX || p.x > width + CORNER_TOLERANCE_PX || p.y > height + CORNER_TOLERANCE_PX) return false;
    }
    return true;
  }

  // Pixels per inch over the print area, on the weaker side (the service's effectivePpi).
  function ppiOf(crop, width, height, angle, spec) {
    var area = spec.print_area_mm;
    var size = cropSize(crop, width, height, angle);
    return Math.floor(Math.min(size.width / (area.width / MM_PER_INCH), size.height / (area.height / MM_PER_INCH)));
  }

  function qualityOf(ppi, t) {
    if (ppi >= t.good) return 'good';
    if (ppi >= t.warn) return 'warn';
    if (ppi >= t.severe) return 'low';
    return 'very_low';
  }

  function qualityText(quality) {
    var s = strings();
    return { good: s.quality_good, warn: s.quality_warn, low: s.quality_low, very_low: s.quality_very_low }[quality] || '';
  }

  function degreesText(angle) {
    return String(Math.abs(angle));
  }

  // "7.5° clockwise" (visible) and "7.5 degrees clockwise" (read out).
  function angleLabel(angle, spoken) {
    var s = strings();
    var d = degreesText(angle);
    if (angle === 0) return spoken ? s.angle_value_zero : s.angle_zero;
    if (angle === 180) return spoken ? s.angle_value_180 : s.angle_180;
    return fill(angle > 0 ? (spoken ? s.angle_value_cw : s.angle_cw) : spoken ? s.angle_value_acw : s.angle_acw, { degrees: d });
  }

  // Draws a crop of the photo turned by angle into outW x outH device pixels: exactly the print file's geometry.
  // img may be a smaller preview of the photo; it is drawn at the photo's own size (width x height).
  function drawCrop(canvas, img, width, height, angle, crop, outW, outH) {
    canvas.width = outW;
    canvas.height = outH;
    var ctx = canvas.getContext('2d');
    var box = turnedBox(width, height, angle);
    var t = trig(angle);
    var k = outW / (crop.w * box.width);
    ctx.save();
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, outW, outH);
    ctx.imageSmoothingQuality = 'high';
    ctx.translate(-crop.x * box.width * k, -crop.y * box.height * k);
    ctx.scale(k, k);
    ctx.translate(box.width / 2, box.height / 2);
    ctx.transform(t.c, t.s, -t.s, t.c, 0, 0);
    ctx.drawImage(img, -width / 2, -height / 2, width, height);
    ctx.restore();
  }

  function loadImage(url) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.decoding = 'async';
      img.referrerPolicy = 'no-referrer';
      img.onload = function () {
        resolve(img);
      };
      img.onerror = function () {
        reject({ code: 'preview_failed' });
      };
      img.src = url;
    });
  }

  // The photo as stored in _cgl_data (v4 or v3) in v4 terms: angle and the upright photo's size.
  function photoRef(ref) {
    if (!ref || !ref.asset || !ref.crop) return null;
    var angle;
    var master;
    if (typeof ref.rotation_degrees === 'number') {
      angle = wrapAngle(ref.rotation_degrees, 0.5);
      master = ref.master && ref.master.w > 0 ? { w: ref.master.w, h: ref.master.h } : null;
    } else {
      angle = fromV3(ref.rotate || 0);
      var src = ref.src || { w: 0, h: 0 };
      master = src.w > 0 ? (angle === 90 || angle === -90 ? { w: src.h, h: src.w } : { w: src.w, h: src.h }) : null;
    }
    return { asset: ref.asset, angle: angle, crop: ref.crop, master: master, ppi: ref.ppi, quality: ref.quality };
  }

  // ---- tokens (this browser's proof of upload) ------------------------------------------------------------

  function allTokens() {
    var all = readJson(local, TOKENS_KEY, {});
    return all && typeof all === 'object' ? all : {};
  }

  function saveToken(asset, token, service, extra) {
    var all = allTokens();
    var cutoff = Date.now() - TOKEN_DAYS * 86400000;
    Object.keys(all).forEach(function (key) {
      if (!all[key] || all[key].at < cutoff) delete all[key];
    });
    all[asset] = Object.assign({}, all[asset] || {}, { t: token, s: service, at: Date.now() }, extra || {});
    writeJson(local, TOKENS_KEY, all);
  }

  function tokenFor(asset) {
    var entry = allTokens()[asset];
    return entry && entry.t && entry.s ? entry : null;
  }

  function forgetToken(asset) {
    var all = allTokens();
    if (all[asset]) {
      delete all[asset];
      writeJson(local, TOKENS_KEY, all);
    }
  }

  // The upload service this browser last used, for the cart's photo check when a line has no token here.
  function rememberService(base) {
    if (/^https:\/\//.test(base || '')) writeJson(local, SERVICE_KEY, { s: base });
  }

  function knownService() {
    var saved = readJson(local, SERVICE_KEY, null);
    return saved && typeof saved.s === 'string' ? saved.s : '';
  }

  // ---- the upload service -----------------------------------------------------------------------------------

  var services = {};

  function Service(base) {
    this.base = String(base || '').replace(/\/+$/, '');
    this.sessions = {};
  }

  Service.get = function (base) {
    var key = String(base || '').replace(/\/+$/, '');
    if (!services[key]) services[key] = new Service(key);
    return services[key];
  };

  Service.prototype.call = function (method, path, options) {
    options = options || {};
    var headers = { Accept: 'application/json' };
    if (options.token) headers['X-CGL-Upload-Token'] = options.token;
    var init = { method: method, headers: headers, mode: 'cors', credentials: 'omit', referrerPolicy: 'no-referrer', cache: 'no-store' };
    if (options.json !== undefined) {
      headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(options.json);
    }
    return fetch(this.base + path, init).then(
      function (response) {
        return response
          .json()
          .catch(function () {
            return {};
          })
          .then(function (data) {
            if (response.ok) return data;
            throw { code: (data && data.error) || 'server_error', status: response.status };
          });
      },
      function () {
        throw { code: 'network' };
      }
    );
  };

  // An upload session covers one profile and the canvas spec of one variant.
  Service.prototype.session = function (profile, variant, pieces, fresh) {
    var key = 'cgl-photo-session:' + this.base + '|' + profile + '|' + variant;
    if (!fresh) {
      var cached = this.sessions[key] || readJson(tab, key, null);
      if (cached && cached.expires_at * 1000 - Date.now() > 5 * 60000 && cached.pieces >= pieces) {
        this.sessions[key] = cached;
        return Promise.resolve(cached);
      }
    }
    return this.call('POST', '/v1/sessions', { json: { profile: profile, variant: String(variant), pieces: pieces } }).then((data) => {
      var s = { token: data.session, expires_at: data.expires_at, spec: data.spec, limits: data.limits, pieces: pieces, variant: String(variant) };
      this.sessions[key] = s;
      writeJson(tab, key, s);
      return s;
    });
  };

  Service.prototype.send = function (session, file, onProgress, holder) {
    var base = this.base;
    return new Promise(function (resolve, reject) {
      var xhr = new XMLHttpRequest();
      holder.xhr = xhr;
      xhr.open('POST', base + '/v1/uploads');
      xhr.setRequestHeader('Authorization', 'Bearer ' + session.token);
      xhr.setRequestHeader('Content-Type', 'application/octet-stream');
      xhr.timeout = 180000;
      xhr.upload.onprogress = function (event) {
        if (event.lengthComputable) onProgress(event.loaded / event.total);
      };
      xhr.upload.onload = function () {
        onProgress(1);
      };
      xhr.onload = function () {
        var data = {};
        try {
          data = JSON.parse(xhr.responseText || '{}');
        } catch (e) {
          data = {};
        }
        if (xhr.status === 201 && data.upload) resolve(data.upload);
        else reject({ code: data.error || 'server_error', status: xhr.status, max: data.max_bytes });
      };
      xhr.onerror = function () {
        reject({ code: 'network' });
      };
      xhr.ontimeout = function () {
        reject({ code: 'network' });
      };
      xhr.onabort = function () {
        reject({ code: 'aborted' });
      };
      xhr.send(file);
    });
  };

  // One retry with a new session when the old one ran out (time or tries).
  Service.prototype.upload = function (profile, variant, pieces, file, onProgress, holder) {
    var attempt = (fresh) =>
      this.session(profile, variant, pieces, fresh).then((session) => {
        if (holder.cancelled) throw { code: 'aborted' };
        return this.send(session, file, onProgress, holder).then(function (upload) {
          return { session: session, upload: upload };
        });
      });
    return attempt(false).catch(function (error) {
      if (error && (error.code === 'session_expired' || error.code === 'session_invalid' || error.code === 'too_many_files') && !holder.cancelled) {
        return attempt(true);
      }
      throw error;
    });
  };

  Service.prototype.crop = function (asset, token, body) {
    return this.call('PUT', '/v1/uploads/' + asset + '/crop', { token: token, json: body });
  };

  Service.prototype.link = function (asset, token) {
    return this.call('POST', '/v1/uploads/' + asset + '/link', { token: token });
  };

  Service.prototype.carted = function (asset, token) {
    return this.call('POST', '/v1/uploads/' + asset + '/carted', { token: token });
  };

  Service.prototype.remove = function (asset, token) {
    return this.call('DELETE', '/v1/uploads/' + asset, { token: token });
  };

  // Whether photos still exist ('ok' or 'gone'). Needs no token and doesn't count as activity on the photo.
  Service.prototype.status = function (ids) {
    return this.call('POST', '/v1/uploads/status', { json: { ids: ids } }).then(function (data) {
      return (data && data.status) || {};
    });
  };

  function errorText(error, maxMb) {
    var s = strings();
    var code = error && error.code;
    var map = {
      too_large: fill(s.error_too_large, { max: maxMb }),
      empty: s.error_empty,
      unsupported_type: s.error_type,
      unreadable: s.error_unreadable,
      too_many_pixels: s.error_pixels,
      too_small: s.error_small,
      rate_limited: s.error_rate,
      too_many_files: s.error_rate,
      busy: s.error_busy,
      network: s.error_network,
      upload_gone: s.error_gone,
      preview_failed: s.error_network,
    };
    return map[code] || s.error_generic;
  }

  // ---- drafts (refresh recovery for this tab) ----------------------------------------------------------------

  function draftKey(host) {
    return 'cgl-photo-draft:' + (host.dataset.productId || '') + ':' + (host.dataset.profile || '') + '@v' + (host.dataset.version || '1');
  }

  function readDraft(host) {
    var d = readJson(tab, draftKey(host), null);
    return d && typeof d === 'object' && d.sets ? d : { sets: {} };
  }

  function writeDraft(host, draft) {
    writeJson(tab, draftKey(host), draft);
  }

  function clearDraft(host) {
    writeJson(tab, draftKey(host), null);
  }

  // ---- <cgl-photo> ------------------------------------------------------------------------------------------

  class CglPhotoField extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.host = this.closest('cgl-personalisation');
      this.input = this.querySelector('input[type="file"]');
      this.parts = {};
      this.querySelectorAll('[data-ph]').forEach((el) => {
        this.parts[el.dataset.ph] = el;
      });
      this.photo = null;
      this.pending = null;
      this.failure = '';
      this.note = '';
      this.image = null;
      if (this.host) rememberService(this.host.dataset.uploadService);

      this.input.addEventListener('change', () => {
        var file = this.input.files && this.input.files[0];
        this.input.value = '';
        if (file) this.pick(file);
      });
      this.addEventListener('click', (event) => {
        var button = event.target.closest('button[data-ph-action]');
        if (!button || !this.contains(button)) return;
        var action = button.dataset.phAction;
        if (action === 'choose') this.input.click();
        else if (action === 'adjust') this.openCrop(button);
        else if (action === 'remove') this.remove();
        else if (action === 'cancel') this.cancelUpload();
      });
      if (this.parts.accept) {
        this.parts.accept.querySelector('input').addEventListener('change', (event) => {
          if (!this.photo) return;
          this.photo.accepted = event.target.checked;
          this.saveDraft();
          this.emit();
        });
      }
      this.render();
      if (!this.editing()) this.restoreDraft();
    }

    // ---- context from the personalisation block ----

    get index() {
      var set = this.closest('[data-cgl-set]');
      return set ? set.dataset.index : '0';
    }

    service() {
      return Service.get(this.host ? this.host.dataset.uploadService : '');
    }

    profile() {
      return this.host ? this.host.dataset.profile + '@v' + (this.host.dataset.version || '1') : '';
    }

    // 4: mini-canvas@v4 and later (any angle). 3: mini-canvas@v3 (quarter turns, "rotate").
    schema() {
      var v = this.host ? parseInt(this.host.dataset.version, 10) : 0;
      return v >= 4 ? 4 : 3;
    }

    variant() {
      if (this.host && typeof this.host.variantId === 'function') return this.host.variantId();
      return '';
    }

    pieces() {
      var n = this.host ? parseInt(this.host.dataset.pieces, 10) : 1;
      return n > 0 ? Math.min(n, 10) : 1;
    }

    maxMb() {
      return parseInt(this.dataset.maxMb, 10) || 20;
    }

    editing() {
      var params;
      try {
        params = new URLSearchParams(window.location.search);
      } catch (e) {
        return false;
      }
      return !!params.get('cgl_edit');
    }

    // ---- what assets/cgl-personalisation.js reads ----

    status() {
      if (this.pending) return { status: 'busy' };
      if (!this.photo) return this.failure ? { status: 'failed', message: this.failure } : { status: 'empty' };
      if (!this.photo.confirmed) return { status: 'unconfirmed' };
      return { status: 'ready', needsAccept: this.photo.quality === 'very_low', accepted: !!this.photo.accepted };
    }

    // The photo as it goes into _cgl_data: opaque asset ID and the confirmed transform. No links.
    // mini-canvas@v4: rotation_degrees and the upright photo's size. mini-canvas@v3: rotate and the turned size.
    value() {
      var p = this.photo;
      if (!p || !p.confirmed) return null;
      if (this.schema() === 4) {
        return { asset: p.asset, rotation_degrees: p.angle, crop: p.crop, master: { w: p.width, h: p.height }, ppi: p.ppi, quality: p.quality };
      }
      var size = turnedBox(p.width, p.height, p.angle);
      return { asset: p.asset, rotate: toV3(p.angle), crop: p.crop, src: { w: size.width, h: size.height }, ppi: p.ppi, quality: p.quality };
    }

    spec() {
      return this.photo ? this.photo.spec : null;
    }

    assets() {
      return this.photo ? [{ asset: this.photo.asset, token: this.photo.token, service: this.photo.service }] : [];
    }

    focusControl() {
      var target = this.querySelector('button[data-ph-action]:not([hidden])');
      var visible = Array.prototype.filter.call(this.querySelectorAll('button[data-ph-action], input[type="checkbox"]'), function (el) {
        return el.offsetParent !== null;
      });
      (visible[0] || target || this).focus({ preventScroll: true });
    }

    // After Add to cart: start again for the next canvas. The photo stays on the cart line.
    clear() {
      if (this.photo) saveToken(this.photo.asset, this.photo.token, this.photo.service, { cart: true });
      this.photo = null;
      this.failure = '';
      this.note = '';
      this.image = null;
      this.render();
    }

    // Editing a cart line made with an earlier profile version: its photo was confirmed under the old contract,
    // so it has to be chosen again (the service would not accept it on a line of the new version).
    oldVersion() {
      this.photo = null;
      this.note = strings().edit_old_version || strings().edit_gone;
      this.render();
      this.emit();
    }

    // Editing a cart line: show its photo and crop. Without this browser's token the photo can't be reopened.
    prefill(raw) {
      var ref = photoRef(raw);
      if (!ref) return Promise.resolve(false);
      var saved = tokenFor(ref.asset);
      if (!saved) {
        this.note = strings().edit_no_token;
        this.render();
        return Promise.resolve(false);
      }
      var service = Service.get(saved.s);
      this.pending = { restoring: true };
      this.render();
      return service
        .link(ref.asset, saved.t)
        .then((data) => {
          this.pending = null;
          var master = ref.master || { w: 0, h: 0 };
          this.photo = {
            asset: ref.asset,
            token: saved.t,
            service: saved.s,
            width: master.w,
            height: master.h,
            preview: data.preview,
            angle: ref.angle,
            crop: ref.crop,
            confirmed: true,
            ppi: ref.ppi,
            quality: ref.quality,
            accepted: ref.quality === 'very_low',
            spec: null,
            variant: this.variant(),
            inCart: true,
          };
          return this.ensureSpec().then(() => {
            this.render();
            this.emit();
            return true;
          });
        })
        .catch((error) => {
          this.pending = null;
          this.note = error && error.code === 'upload_gone' ? strings().edit_gone : strings().edit_no_token;
          this.render();
          this.emit();
          return false;
        });
    }

    // The canvas spec comes from an upload session for the current variant.
    ensureSpec() {
      if (this.photo && this.photo.spec) return Promise.resolve(this.photo.spec);
      return this.service()
        .session(this.profile(), this.variant(), this.pieces(), false)
        .then((session) => {
          if (this.photo) this.photo.spec = session.spec;
          return session.spec;
        });
    }

    // ---- choosing and uploading ----

    precheck(file) {
      var mb = this.maxMb();
      if (file.size === 0) return { code: 'empty' };
      if (file.size > mb * 1000000) return { code: 'too_large' };
      var name = (file.name || '').toLowerCase();
      var okType = /^image\/(jpeg|jpg|pjpeg|png|heic|heif|heic-sequence|heif-sequence)$/.test(file.type || '');
      var okName = /\.(jpe?g|png|heic|heif)$/.test(name);
      // Some phones send no type for HEIC; the service checks the file's real content either way.
      if (!okType && !okName && file.type) return { code: 'unsupported_type' };
      return null;
    }

    pick(file) {
      var pre = this.precheck(file);
      if (pre) {
        this.failure = errorText(pre, this.maxMb());
        if (this.photo) this.note = fill(strings().kept_previous, { reason: this.failure });
        this.render();
        this.emit();
        return;
      }
      var previous = this.photo;
      var holder = { cancelled: false };
      this.pending = holder;
      this.failure = '';
      this.note = '';
      this.progress(0);
      this.render();
      this.emit();
      var variant = this.variant();
      this.service()
        .upload(this.profile(), variant, this.pieces(), file, (fraction) => this.progress(fraction), holder)
        .then((result) => {
          if (this.pending !== holder) return;
          this.pending = null;
          var u = result.upload;
          var base = this.service().base;
          this.photo = {
            asset: u.id,
            token: u.token,
            service: base,
            width: u.width,
            height: u.height,
            preview: u.preview,
            angle: 0,
            crop: u.default_crop,
            confirmed: false,
            ppi: u.ppi,
            quality: u.quality,
            accepted: false,
            spec: result.session.spec,
            variant: variant,
            inCart: false,
          };
          this.image = null;
          saveToken(u.id, u.token, base);
          // The photo it replaces was never in the cart: delete it now rather than wait for the clean-up.
          if (previous && !previous.inCart) this.discard(previous);
          this.saveDraft();
          this.render();
          this.emit();
          this.openCrop(this.parts.adjust);
        })
        .catch((error) => {
          if (this.pending !== holder) return;
          this.pending = null;
          if (error && error.code === 'aborted') {
            this.render();
            this.emit();
            return;
          }
          var text = errorText(error, this.maxMb());
          if (previous) {
            this.photo = previous;
            this.note = fill(strings().kept_previous, { reason: text });
          } else {
            this.failure = text;
          }
          this.render();
          this.emit();
        });
    }

    cancelUpload() {
      var holder = this.pending;
      if (!holder || holder.restoring) return;
      holder.cancelled = true;
      this.pending = null;
      if (holder.xhr) holder.xhr.abort();
      this.render();
      this.emit();
      this.focusControl();
    }

    progress(fraction) {
      var bar = this.parts.progress;
      var text = this.parts.busytext;
      if (!bar || !text) return;
      var s = strings();
      if (fraction >= 1) {
        bar.removeAttribute('value');
        text.textContent = s.checking;
      } else {
        var pct = Math.round(fraction * 100);
        bar.value = pct;
        text.textContent = fill(s.uploading, { percent: pct });
      }
    }

    discard(photo) {
      Service.get(photo.service)
        .remove(photo.asset, photo.token)
        .then(
          function () {
            forgetToken(photo.asset);
          },
          function () {
            /* the service deletes it within a day anyway */
          }
        );
    }

    remove() {
      if (this.pending) return;
      var photo = this.photo;
      this.photo = null;
      this.image = null;
      this.failure = '';
      this.note = '';
      if (photo && !photo.inCart) this.discard(photo);
      this.saveDraft();
      this.render();
      this.emit();
      this.focusControl();
    }

    // ---- crop ----

    openCrop(trigger) {
      if (!this.photo || this.pending) return;
      dialog().open(this, trigger || this.parts.adjust);
    }

    // Resolves to a decoded preview image, renewing the short-lived link when it has run out.
    previewImage() {
      var p = this.photo;
      if (!p) return Promise.reject({ code: 'upload_gone' });
      if (this.image && this.image.asset === p.asset) return Promise.resolve(this.image.img);
      var fresh = p.preview && p.preview.expires_at * 1000 - Date.now() > 60000;
      var linked = fresh
        ? Promise.resolve(p.preview)
        : Service.get(p.service)
            .link(p.asset, p.token)
            .then(function (data) {
              p.preview = data.preview;
              return data.preview;
            });
      return linked
        .then(function (preview) {
          return loadImage(preview.url);
        })
        .then((img) => {
          // A line saved before the photo's size was recorded: the preview has the photo's shape.
          if (!(p.width > 0 && p.height > 0)) {
            p.width = img.naturalWidth;
            p.height = img.naturalHeight;
          }
          this.image = { asset: p.asset, img: img };
          return img;
        });
    }

    // Called by the dialog with the customer's confirmed angle and crop. The service checks it again (no blank
    // corner, print shape, angle on the step grid) and its answer is what is kept.
    saveCrop(angle, crop) {
      var p = this.photo;
      var v4 = this.schema() === 4;
      var body = v4 ? { rotation_degrees: angle, crop: crop, confirmed: true } : { rotate: toV3(angle), crop: crop, confirmed: true };
      return Service.get(p.service)
        .crop(p.asset, p.token, body)
        .then((data) => {
          if (this.photo !== p) return;
          var wasQuality = p.quality;
          p.angle = v4 ? data.rotation_degrees : fromV3(data.rotate);
          if (v4 && data.master) {
            p.width = data.master.w;
            p.height = data.master.h;
          }
          p.crop = data.crop;
          p.ppi = data.ppi;
          p.quality = data.quality;
          p.confirmed = true;
          if (data.quality !== wasQuality || data.quality !== 'very_low') p.accepted = false;
          this.note = '';
          this.saveDraft();
          this.render();
          this.emit();
        });
    }

    // The upload service no longer has the photo (expired or removed): start again.
    gone() {
      this.photo = null;
      this.image = null;
      this.failure = strings().error_gone;
      this.saveDraft();
      this.render();
      this.emit();
    }

    // ---- drafts ----

    saveDraft() {
      if (!this.host || this.editing()) return;
      var draft = readDraft(this.host);
      var p = this.photo;
      if (p && !p.inCart) {
        draft.sets[this.index] = {
          asset: p.asset,
          token: p.token,
          service: p.service,
          width: p.width,
          height: p.height,
          angle: p.angle,
          crop: p.crop,
          confirmed: p.confirmed,
          ppi: p.ppi,
          quality: p.quality,
          accepted: p.accepted,
          variant: p.variant,
        };
      } else {
        delete draft.sets[this.index];
      }
      writeDraft(this.host, draft);
    }

    restoreDraft() {
      if (!this.host) return;
      var saved = readDraft(this.host).sets[this.index];
      if (!saved || !saved.asset || !saved.token) return;
      var holder = { restoring: true };
      this.pending = holder;
      this.render();
      Service.get(saved.service)
        .link(saved.asset, saved.token)
        .then((data) => {
          if (this.pending !== holder) return;
          this.pending = null;
          var angle = typeof saved.angle === 'number' ? saved.angle : fromV3(saved.rotate || 0);
          this.photo = Object.assign({}, saved, { angle: angle, preview: data.preview, spec: null, inCart: false });
          return this.ensureSpec().then(() => {
            this.render();
            this.emit();
          });
        })
        .catch(() => {
          if (this.pending !== holder) return;
          this.pending = null;
          this.photo = null;
          this.note = strings().draft_gone;
          this.saveDraft();
          this.render();
          this.emit();
        });
    }

    // ---- display ----

    emit() {
      this.dispatchEvent(new CustomEvent('change', { bubbles: true }));
    }

    render() {
      var s = strings();
      var parts = this.parts;
      var st = this.status().status;
      var hasPhoto = !!this.photo;
      if (parts.empty) parts.empty.hidden = hasPhoto || !!this.pending;
      if (parts.busy) parts.busy.hidden = !this.pending;
      if (parts.cancel) parts.cancel.hidden = !this.pending || !!this.pending.restoring;
      if (this.pending && this.pending.restoring && parts.busytext) {
        parts.busytext.textContent = s.restoring;
        if (parts.progress) parts.progress.removeAttribute('value');
      }
      if (parts.done) parts.done.hidden = !hasPhoto || !!this.pending;
      if (parts.note) {
        parts.note.textContent = this.note;
        parts.note.hidden = !this.note;
      }
      if (!hasPhoto) {
        if (parts.choose) parts.choose.textContent = s.choose;
        return;
      }
      var p = this.photo;
      if (parts.state) parts.state.textContent = p.confirmed ? s.state_ready : s.state_unconfirmed;
      if (parts.adjust) {
        parts.adjust.textContent = p.confirmed ? s.adjust : s.position;
        parts.adjust.classList.toggle('button--secondary', p.confirmed);
      }
      var quality = p.confirmed ? p.quality : null;
      if (parts.quality) {
        parts.quality.textContent = quality ? qualityText(quality) : '';
        parts.quality.hidden = !quality;
        parts.quality.dataset.quality = quality || '';
      }
      if (parts.accept) {
        parts.accept.hidden = !(p.confirmed && p.quality === 'very_low');
        parts.accept.querySelector('input').checked = !!p.accepted;
      }
      this.classList.toggle('cgl-ph--unconfirmed', st === 'unconfirmed');
      this.drawThumb();
    }

    drawThumb() {
      var canvas = this.parts.thumb;
      var p = this.photo;
      if (!canvas || !p) return;
      this.previewImage().then(
        (img) => {
          if (this.photo !== p) return;
          var size = cropSize(p.crop, p.width, p.height, p.angle);
          var aspect = p.spec ? p.spec.aspect : size.width / size.height;
          var cssW = canvas.clientWidth || 96;
          var dpr = Math.min(window.devicePixelRatio || 1, 3);
          var w = Math.round(cssW * dpr);
          drawCrop(canvas, img, p.width, p.height, p.angle, p.crop, w, Math.round(w / aspect));
          canvas.hidden = false;
        },
        (error) => {
          if (error && error.code === 'upload_gone' && this.photo === p) this.gone();
        }
      );
    }
  }

  // ---- the crop dialog (one per page) ----------------------------------------------------------------------
  //
  // The dialog keeps what the customer asked for (zoom and the point of the photo at the frame's centre) apart
  // from what is shown: at an angle that needs more zoom to fill the frame, the zoom shown is raised to that
  // minimum and the centre is moved in only as far as the frame needs. Turning back gives the request back.

  var dialogInstance = null;

  function dialog() {
    if (!dialogInstance) dialogInstance = new CropDialog(document.querySelector('dialog[data-cgl-crop]'));
    return dialogInstance;
  }

  function CropDialog(el) {
    this.el = el;
    this.parts = {};
    if (!el) return;
    el.querySelectorAll('[data-crop]').forEach((part) => {
      this.parts[part.dataset.crop] = part;
    });
    this.pointers = new Map();
    this.canvas = this.parts.stage;
    this.redraw = this.redraw.bind(this);

    el.addEventListener('click', (event) => {
      var button = event.target.closest('button[data-crop-action]');
      if (!button) return;
      var action = button.dataset.cropAction;
      if (action === 'cancel') this.close(false);
      else if (action === 'turn-cw') this.turnBy(90, true);
      else if (action === 'turn-acw') this.turnBy(-90, true);
      else if (action === 'fine-cw') this.turnBy(this.rule.fine_step || 1, true);
      else if (action === 'fine-acw') this.turnBy(-(this.rule.fine_step || 1), true);
      else if (action === 'reset-rotation') this.resetRotation();
      else if (action === 'reset') this.reset();
      else if (action === 'zoom-in') this.zoomBy(1.25);
      else if (action === 'zoom-out') this.zoomBy(0.8);
      else if (action === 'next') this.step('check');
      else if (action === 'back') this.step('edit');
      else if (action === 'confirm') this.confirm();
    });
    el.addEventListener('cancel', (event) => {
      event.preventDefault();
      if (!this.saving) this.close(false);
    });
    this.parts.zoom.addEventListener('input', () => this.onZoomSlider());
    var stage = this.canvas;
    stage.addEventListener('pointerdown', (e) => this.onDown(e));
    stage.addEventListener('pointermove', (e) => this.onMove(e));
    stage.addEventListener('pointerup', (e) => this.onUp(e));
    stage.addEventListener('pointercancel', (e) => this.onUp(e));
    stage.addEventListener('wheel', (e) => this.onWheel(e), { passive: false });
    stage.addEventListener('keydown', (e) => this.onKey(e));
    var ruler = this.parts.ruler;
    if (ruler) {
      ruler.addEventListener('pointerdown', (e) => this.onRulerDown(e));
      ruler.addEventListener('pointermove', (e) => this.onRulerMove(e));
      ruler.addEventListener('pointerup', (e) => this.onRulerUp(e));
      ruler.addEventListener('pointercancel', (e) => this.onRulerUp(e));
      ruler.addEventListener('keydown', (e) => this.onRulerKey(e));
      ruler.addEventListener('wheel', (e) => this.onRulerWheel(e), { passive: false });
      ruler.addEventListener('focus', () => this.straighten(true));
      ruler.addEventListener('blur', () => this.straighten(false));
    }
    window.addEventListener('resize', () => {
      if (this.el.open) this.layout();
    });
  }

  CropDialog.prototype.open = function (field, trigger) {
    if (!this.el) return;
    var p = field.photo;
    this.field = field;
    this.trigger = trigger;
    this.photo = p;
    this.saving = false;
    this.dirty = false;
    this.drag = null;
    this.zoomNoted = false;
    this.error('');
    this.parts.stage.setAttribute('aria-busy', 'true');
    this.step('edit');
    if (typeof this.el.showModal === 'function') this.el.showModal();
    else this.el.setAttribute('open', '');
    document.documentElement.classList.add('cgl-crop-open');
    Promise.all([field.previewImage(), field.ensureSpec()]).then(
      (results) => {
        if (this.field !== field) return;
        this.img = results[0];
        this.spec = results[1];
        this.quarterOnly = field.schema() !== 4;
        this.rule = Object.assign({}, DEFAULT_ROTATION, this.spec.rotation || {});
        this.el.toggleAttribute('data-quarter-only', this.quarterOnly);
        this.parts.size.textContent = fill(strings().frame_size, {
          width: this.spec.face_mm.width,
          height: this.spec.face_mm.height,
        });
        if (this.parts.safehint) this.parts.safehint.hidden = !(this.spec.safe_margin_mm > 0);
        this.start(p.angle || 0, p.confirmed ? p.crop : null);
        this.parts.stage.removeAttribute('aria-busy');
        this.parts.stage.focus({ preventScroll: true });
      },
      (error) => {
        if (error && error.code === 'upload_gone') {
          this.close(false);
          field.gone();
          return;
        }
        this.error(strings().error_network);
      }
    );
  };

  CropDialog.prototype.close = function (saved) {
    if (this.el.open) {
      if (typeof this.el.close === 'function') this.el.close();
      else this.el.removeAttribute('open');
    }
    document.documentElement.classList.remove('cgl-crop-open');
    var field = this.field;
    var trigger = this.trigger;
    this.field = null;
    this.img = null;
    this.drag = null;
    this.pointers.clear();
    if (field) {
      field.render();
      var target = trigger && trigger.isConnected && trigger.offsetParent !== null ? trigger : null;
      if (target) target.focus({ preventScroll: true });
      else field.focusControl();
    }
    return saved;
  };

  CropDialog.prototype.step = function (name) {
    this.parts.edit.hidden = name !== 'edit';
    this.parts.check.hidden = name !== 'check';
    this.parts.title.textContent = name === 'edit' ? strings().crop_title : strings().check_title;
    if (name === 'check') {
      this.drawResult();
      this.parts.confirm.focus({ preventScroll: true });
    } else if (this.img) {
      this.layout();
      this.parts.stage.focus({ preventScroll: true });
    }
  };

  // ---- the crop model ----

  // Half the crop's width, in photo pixels, of the largest crop at the print shape that fits at this angle
  // (centred). Shrunk by a millionth off the quarter turns, as the service's defaultCrop is.
  CropDialog.prototype.capHalf = function (angle) {
    var W = this.photo.width;
    var H = this.photo.height;
    var aspect = this.spec.aspect;
    var box = turnedBox(W, H, angle);
    if (isQuarter(angle)) return Math.min(box.width / 2, (box.height / 2) * aspect);
    var t = trig(angle);
    var ac = Math.abs(t.c);
    var as = Math.abs(t.s);
    return Math.min(W / 2 / (ac + as / aspect), H / 2 / (as + ac / aspect)) * (1 - 1e-6);
  };

  // Starts the model from an angle and, when there is one, a confirmed crop.
  CropDialog.prototype.start = function (angle, crop) {
    var W = this.photo.width;
    var H = this.photo.height;
    this.angle = this.quarterOnly ? wrapAngle(Math.round(angle / 90) * 90, 90) : wrapAngle(angle, this.rule.step);
    this.ref = this.capHalf(0); // zoom 1 is the largest crop of the upright photo
    this.maxZoomBase = Math.max(1, Math.min(MAX_ZOOM, this.ref / (MIN_CROP_PX / 2)));
    this.initialCrop = null;
    if (crop && crop.w > 0) {
      var box = turnedBox(W, H, this.angle);
      var centre = boxToMaster((crop.x + crop.w / 2) * box.width, (crop.y + crop.h / 2) * box.height, W, H, this.angle);
      var zoom = this.ref / ((crop.w * box.width) / 2);
      // A crop saved as wide as its angle allowed is reopened as "as wide as possible", so turning back widens.
      if (Math.abs(zoom - this.ref / this.capHalf(this.angle)) < 1e-4) zoom = 0;
      this.want = { zoom: zoom, x: centre.x, y: centre.y };
      this.initialCrop = cleanCrop(crop);
    } else {
      this.want = { zoom: 0, x: W / 2, y: H / 2 };
    }
    this.solve();
    this.syncControls();
    this.layout();
  };

  // What is shown for what was asked: the zoom raised to this angle's minimum, the centre kept inside the
  // photo by the frame's reach at this angle. Sets this.view (zoom, half sizes a and b, centre) and this.crop.
  CropDialog.prototype.solve = function () {
    var W = this.photo.width;
    var H = this.photo.height;
    var aspect = this.spec.aspect;
    var angle = this.angle;
    var minZoom = this.ref / this.capHalf(angle);
    var maxZoom = Math.max(minZoom, this.maxZoomBase);
    var zoom = clamp(this.want.zoom, minZoom, maxZoom);
    var a = this.ref / zoom;
    var b = a / aspect;
    var t = trig(angle);
    var ex = a * Math.abs(t.c) + b * Math.abs(t.s);
    var ey = a * Math.abs(t.s) + b * Math.abs(t.c);
    var cx = ex >= W / 2 ? W / 2 : clamp(this.want.x, ex, W - ex);
    var cy = ey >= H / 2 ? H / 2 : clamp(this.want.y, ey, H - ey);
    this.model = { zoom: zoom, minZoom: minZoom, maxZoom: maxZoom, raised: minZoom > 1 + 1e-6 && this.want.zoom < minZoom - 1e-6, a: a, b: b, x: cx, y: cy };
    var box = turnedBox(W, H, angle);
    var c = masterToBox(cx, cy, W, H, angle);
    this.crop = { x: (c.x - a) / box.width, y: (c.y - b) / box.height, w: (2 * a) / box.width, h: (2 * b) / box.height };
  };

  // The crop that will be sent: unchanged from the confirmed one unless the customer changed something.
  CropDialog.prototype.finalCrop = function () {
    if (!this.dirty && this.initialCrop) return this.initialCrop;
    return cleanCrop(this.crop);
  };

  CropDialog.prototype.changed = function () {
    this.dirty = true;
    this.solve();
    this.syncControls();
    this.update();
  };

  CropDialog.prototype.syncControls = function () {
    var m = this.model;
    var zoom = this.parts.zoom;
    zoom.min = String(Math.floor(m.minZoom * 1000) / 1000);
    zoom.max = String(Math.ceil(m.maxZoom * 1000) / 1000);
    zoom.step = '0.001';
    zoom.disabled = m.maxZoom - m.minZoom < 0.001;
    zoom.value = String(m.zoom);
    var label = angleLabel(this.angle, false);
    if (this.parts.angle) this.parts.angle.textContent = label;
    var ruler = this.parts.ruler;
    if (ruler) {
      ruler.setAttribute('aria-valuenow', String(this.angle));
      ruler.setAttribute('aria-valuetext', angleLabel(this.angle, true));
      ruler.setAttribute('aria-valuemin', String(round6(this.rule.min + this.rule.step)));
      ruler.setAttribute('aria-valuemax', String(this.rule.max));
    }
    if (this.parts.zoomnote) {
      // Said once when turning first needs more zoom, then kept on screen while it applies.
      this.parts.zoomnote.hidden = !m.raised;
      if (m.raised && !this.zoomNoted) {
        this.zoomNoted = true;
        this.announce(strings().min_zoom_note);
      }
      if (!m.raised) this.zoomNoted = false;
    }
    this.drawRuler();
  };

  CropDialog.prototype.setAngle = function (angle, announce) {
    var next = this.quarterOnly ? wrapAngle(Math.round(angle / 90) * 90, 90) : wrapAngle(angle, this.rule.step);
    if (next === this.angle) return;
    this.angle = next;
    this.changed();
    if (announce) this.announce(fill(strings().rotated, { value: angleLabel(this.angle, true) }));
  };

  CropDialog.prototype.turnBy = function (degrees, announce) {
    if (!this.img) return;
    this.setAngle(this.angle + degrees, announce);
  };

  CropDialog.prototype.resetRotation = function () {
    if (!this.img) return;
    this.setAngle(0, false);
    this.announce(strings().rotation_reset_done);
  };

  CropDialog.prototype.reset = function () {
    if (!this.img) return;
    this.angle = 0;
    this.want = { zoom: 0, x: this.photo.width / 2, y: this.photo.height / 2 };
    this.changed();
    this.announce(strings().reset_done);
  };

  CropDialog.prototype.announce = function (text) {
    if (this.parts.live) this.parts.live.textContent = text;
  };

  // ---- drawing ----

  CropDialog.prototype.layout = function () {
    if (!this.img || !this.spec) return;
    var canvas = this.canvas;
    var box = this.parts.stagebox;
    var cssW = Math.max(200, Math.min(box.clientWidth || 320, 560));
    var margin = Math.round(cssW * 0.08);
    var frameW = cssW - margin * 2;
    var frameH = frameW / this.spec.aspect;
    var maxH = Math.max(200, window.innerHeight * 0.42);
    if (frameH + margin * 2 > maxH) {
      frameH = maxH - margin * 2;
      frameW = frameH * this.spec.aspect;
    }
    var cssH = Math.round(frameH + margin * 2);
    var dpr = Math.min(window.devicePixelRatio || 1, 3);
    canvas.style.width = cssW + 'px';
    canvas.style.height = cssH + 'px';
    canvas.width = Math.round(cssW * dpr);
    canvas.height = Math.round(cssH * dpr);
    this.view = { w: cssW, h: cssH, dpr: dpr, frame: { x: (cssW - frameW) / 2, y: (cssH - frameH) / 2, w: frameW, h: frameH } };
    canvas.dataset.frame = Math.round(frameW) + 'x' + Math.round(frameH);
    this.update();
    this.drawRuler();
  };

  CropDialog.prototype.schedule = function () {
    if (this.frameRequested) return;
    this.frameRequested = true;
    window.requestAnimationFrame(this.redraw);
  };

  CropDialog.prototype.update = function () {
    this.schedule();
    var quality = qualityOf(ppiOf(this.finalCrop(), this.photo.width, this.photo.height, this.angle, this.spec), this.spec.quality_ppi);
    this.parts.quality.textContent = qualityText(quality);
    this.parts.quality.dataset.quality = quality;
    this.canvas.dataset.angle = String(this.angle);
  };

  CropDialog.prototype.straighten = function (on) {
    this.straightening = on;
    if (this.parts.ruler) this.parts.ruler.classList.toggle('is-active', on);
    if (this.img) this.schedule();
  };

  CropDialog.prototype.redraw = function () {
    this.frameRequested = false;
    if (!this.img || !this.view || !this.model) return;
    var v = this.view;
    var m = this.model;
    var ctx = this.canvas.getContext('2d');
    var f = v.frame;
    var k = f.w / (2 * m.a);
    var t = trig(this.angle);
    ctx.setTransform(v.dpr, 0, 0, v.dpr, 0, 0);
    ctx.fillStyle = '#e9e9e9';
    ctx.fillRect(0, 0, v.w, v.h);
    ctx.save();
    ctx.translate(f.x + f.w / 2, f.y + f.h / 2);
    ctx.scale(k, k);
    ctx.transform(t.c, t.s, -t.s, t.c, 0, 0);
    ctx.translate(-m.x, -m.y);
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(this.img, 0, 0, this.photo.width, this.photo.height);
    ctx.restore();
    // Outside the print area: dimmed (not printed).
    ctx.fillStyle = 'rgba(20, 20, 20, 0.6)';
    ctx.beginPath();
    ctx.rect(0, 0, v.w, v.h);
    ctx.rect(f.x + f.w, f.y, -f.w, f.h);
    ctx.fill('evenodd');
    // Bleed or wrap that is printed but may not show on the face (none on the current canvas).
    var inset = this.spec.face_inset || { x: 0, y: 0 };
    if (inset.x > 0 || inset.y > 0) {
      ctx.fillStyle = 'rgba(255, 255, 255, 0.35)';
      ctx.beginPath();
      ctx.rect(f.x, f.y, f.w, f.h);
      ctx.rect(f.x + f.w * (1 - inset.x), f.y + f.h * inset.y, -f.w * (1 - 2 * inset.x), f.h * (1 - 2 * inset.y));
      ctx.fill('evenodd');
    }
    // A grid while straightening, to line the photo up against.
    if (this.straightening || this.drag) {
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.55)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (var i = 1; i < 3; i += 1) {
        ctx.moveTo(f.x + (f.w * i) / 3, f.y);
        ctx.lineTo(f.x + (f.w * i) / 3, f.y + f.h);
        ctx.moveTo(f.x, f.y + (f.h * i) / 3);
        ctx.lineTo(f.x + f.w, f.y + (f.h * i) / 3);
      }
      ctx.stroke();
    }
    // Safe area for faces and text, measured in from the visible face's edge. A guide only: nothing is cropped
    // to it (the margin is an estimate until the E1 test prints).
    if (typeof this.spec.safe_margin_mm === 'number' && this.spec.safe_margin_mm > 0) {
      var sx = (inset.x + this.spec.safe_margin_mm / this.spec.print_area_mm.width) * f.w;
      var sy = (inset.y + this.spec.safe_margin_mm / this.spec.print_area_mm.height) * f.h;
      ctx.setLineDash([6, 4]);
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.lineWidth = 1;
      ctx.strokeRect(f.x + sx, f.y + sy, f.w - 2 * sx, f.h - 2 * sy);
      ctx.setLineDash([]);
    }
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.strokeRect(f.x - 1, f.y - 1, f.w + 2, f.h + 2);
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.6)';
    ctx.lineWidth = 1;
    ctx.strokeRect(f.x - 2.5, f.y - 2.5, f.w + 5, f.h + 5);
  };

  // The rotation scale: a ruler that moves under a fixed centre mark, one tick per degree.
  CropDialog.prototype.drawRuler = function () {
    var canvas = this.parts.rulercanvas;
    if (!canvas || this.quarterOnly || !this.el.open) return;
    var cssW = canvas.clientWidth || 300;
    var cssH = canvas.clientHeight || 44;
    var dpr = Math.min(window.devicePixelRatio || 1, 3);
    if (canvas.width !== Math.round(cssW * dpr) || canvas.height !== Math.round(cssH * dpr)) {
      canvas.width = Math.round(cssW * dpr);
      canvas.height = Math.round(cssH * dpr);
    }
    var ctx = canvas.getContext('2d');
    var style = window.getComputedStyle(this.parts.ruler);
    var ink = style.color || '#121212';
    var snaps = (this.rule.snap || []).map(function (s) {
      return wrapAngle(s, 0.5);
    });
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cssW, cssH);
    var cx = cssW / 2;
    var half = cx / RULER_PX_PER_DEGREE;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    for (var d = Math.ceil(this.angle - half); d <= Math.floor(this.angle + half); d += 1) {
      var x = cx + (d - this.angle) * RULER_PX_PER_DEGREE;
      var value = wrapAngle(d, 1);
      var snap = snaps.indexOf(value) > -1;
      var len = value % 15 === 0 ? 16 : value % 5 === 0 ? 11 : 6;
      ctx.globalAlpha = Math.max(0.3, 1 - Math.abs(x - cx) / cx);
      ctx.strokeStyle = ink;
      ctx.lineWidth = snap ? 2 : 1;
      ctx.beginPath();
      ctx.moveTo(Math.round(x) + 0.5, cssH - 4);
      ctx.lineTo(Math.round(x) + 0.5, cssH - 4 - len);
      ctx.stroke();
      if (value % 15 === 0) {
        ctx.fillStyle = ink;
        ctx.font = (snap ? '600 ' : '') + '11px ' + (style.fontFamily || 'sans-serif');
        ctx.fillText((value < 0 ? '−' : '') + Math.abs(value) + '°', x, 2);
      }
    }
    ctx.globalAlpha = 1;
    ctx.fillStyle = ink;
    ctx.beginPath();
    ctx.moveTo(cx - 6, cssH);
    ctx.lineTo(cx + 6, cssH);
    ctx.lineTo(cx, cssH - 8);
    ctx.closePath();
    ctx.fill();
    ctx.fillRect(cx - 1, cssH - 26, 2, 22);
  };

  // ---- moving and zooming ----

  // Zoom keeping the point at (fx, fy), as fractions of the frame, still.
  CropDialog.prototype.setZoom = function (zoom, fx, fy) {
    var m = this.model;
    var t = trig(this.angle);
    var dX = (fx - 0.5) * 2 * m.a;
    var dY = (fy - 0.5) * 2 * m.b;
    var qx = m.x + dX * t.c + dY * t.s;
    var qy = m.y - dX * t.s + dY * t.c;
    var target = clamp(zoom, m.minZoom, m.maxZoom);
    var a = this.ref / target;
    var b = a / this.spec.aspect;
    dX = (fx - 0.5) * 2 * a;
    dY = (fy - 0.5) * 2 * b;
    // Zooming right out asks for "as wide as this angle allows", so turning back to straight widens again.
    this.want = { zoom: target <= m.minZoom + 1e-6 ? 0 : target, x: qx - (dX * t.c + dY * t.s), y: qy - (-dX * t.s + dY * t.c) };
    this.changed();
  };

  CropDialog.prototype.onZoomSlider = function () {
    if (!this.img) return;
    this.setZoom(parseFloat(this.parts.zoom.value), 0.5, 0.5);
  };

  CropDialog.prototype.zoomBy = function (factor) {
    if (!this.img) return;
    this.setZoom(this.model.zoom * factor, 0.5, 0.5);
  };

  // Moves the photo by (dx, dy) screen pixels: the frame's centre moves the other way over the photo.
  CropDialog.prototype.pan = function (dx, dy) {
    var m = this.model;
    var k = this.view.frame.w / (2 * m.a);
    var t = trig(this.angle);
    var X = -dx / k;
    var Y = -dy / k;
    this.want = { zoom: this.want.zoom, x: m.x + X * t.c + Y * t.s, y: m.y - X * t.s + Y * t.c };
    this.changed();
    // Panning takes the shown position as the new request: nothing builds up past the edge.
    this.want.x = this.model.x;
    this.want.y = this.model.y;
  };

  CropDialog.prototype.framePoint = function (event) {
    var rect = this.canvas.getBoundingClientRect();
    var f = this.view.frame;
    return { x: clamp((event.clientX - rect.left - f.x) / f.w, 0, 1), y: clamp((event.clientY - rect.top - f.y) / f.h, 0, 1) };
  };

  CropDialog.prototype.onDown = function (event) {
    if (!this.img) return;
    this.canvas.setPointerCapture(event.pointerId);
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (this.pointers.size === 2) this.pinch = this.pinchState();
  };

  CropDialog.prototype.pinchState = function () {
    var pts = Array.from(this.pointers.values());
    var dx = pts[0].x - pts[1].x;
    var dy = pts[0].y - pts[1].y;
    return { distance: Math.hypot(dx, dy) || 1, zoom: this.model.zoom, mid: { clientX: (pts[0].x + pts[1].x) / 2, clientY: (pts[0].y + pts[1].y) / 2 } };
  };

  CropDialog.prototype.onMove = function (event) {
    var last = this.pointers.get(event.pointerId);
    if (!last) return;
    var now = { x: event.clientX, y: event.clientY };
    if (this.pointers.size === 1) {
      this.pan(now.x - last.x, now.y - last.y);
      this.pointers.set(event.pointerId, now);
      return;
    }
    this.pointers.set(event.pointerId, now);
    if (this.pointers.size === 2 && this.pinch) {
      var state = this.pinchState();
      var focus = this.framePoint(this.pinch.mid);
      this.setZoom(this.pinch.zoom * (state.distance / this.pinch.distance), focus.x, focus.y);
    }
  };

  CropDialog.prototype.onUp = function (event) {
    this.pointers.delete(event.pointerId);
    if (this.pointers.size < 2) this.pinch = null;
  };

  CropDialog.prototype.onWheel = function (event) {
    if (!this.img) return;
    event.preventDefault();
    var focus = this.framePoint(event);
    this.setZoom(this.model.zoom * Math.exp(-event.deltaY * 0.0015), focus.x, focus.y);
  };

  CropDialog.prototype.onKey = function (event) {
    if (!this.img) return;
    var step = event.shiftKey ? 40 : 8;
    var handled = true;
    // Arrow keys move the photo, as dragging does.
    if (event.key === 'ArrowLeft') this.pan(-step, 0);
    else if (event.key === 'ArrowRight') this.pan(step, 0);
    else if (event.key === 'ArrowUp') this.pan(0, -step);
    else if (event.key === 'ArrowDown') this.pan(0, step);
    else if (event.key === '+' || event.key === '=') this.zoomBy(1.1);
    else if (event.key === '-' || event.key === '_') this.zoomBy(1 / 1.1);
    else handled = false;
    if (handled) event.preventDefault();
  };

  // ---- the rotation scale: drag, keys, wheel ----
  //
  // Dragging is relative (the scale follows the finger, 8 px a degree), so a touch never jumps the angle. The
  // important angles (0, 90, -90, 180) hold the scale for snap_within degrees of further drag: easy to land on
  // exactly, and a half degree either side is still reachable by dragging on. Keys and buttons are exact.

  CropDialog.prototype.onRulerDown = function (event) {
    if (!this.img || this.quarterOnly) return;
    event.preventDefault();
    this.parts.ruler.setPointerCapture(event.pointerId);
    this.parts.ruler.focus({ preventScroll: true });
    this.drag = { id: event.pointerId, x: event.clientX, raw: this.angle, hold: this.snapAt(this.angle), start: this.angle };
    this.straighten(true);
  };

  CropDialog.prototype.snapAt = function (angle) {
    var snaps = this.rule.snap || [];
    for (var i = 0; i < snaps.length; i += 1) if (wrapAngle(snaps[i], 0.5) === wrapAngle(angle, 0.5)) return angle;
    return null;
  };

  CropDialog.prototype.onRulerMove = function (event) {
    var drag = this.drag;
    if (!drag || drag.id !== event.pointerId) return;
    var dx = event.clientX - drag.x;
    drag.x = event.clientX;
    var within = this.rule.snap_within || 0;
    var prev = drag.raw;
    var raw = prev - dx / RULER_PX_PER_DEGREE;
    if (drag.hold !== null) {
      var off = raw - drag.hold;
      if (Math.abs(off) <= within) {
        drag.raw = raw;
        return;
      }
      raw = drag.hold + off - (off > 0 ? within : -within);
      drag.hold = null;
    } else if (within > 0) {
      var snaps = this.rule.snap || [];
      for (var i = 0; i < snaps.length && drag.hold === null; i += 1) {
        for (var turn = -1; turn <= 1; turn += 1) {
          var s = snaps[i] + 360 * turn + Math.round((prev - snaps[i]) / 360) * 360;
          var crossed = (prev - s) * (raw - s) < 0 || raw === s;
          var entered = Math.abs(prev - s) > within && Math.abs(raw - s) <= within;
          if (crossed || entered) {
            drag.hold = s;
            raw = s;
            this.pulse();
            break;
          }
        }
      }
    }
    drag.raw = raw;
    this.setAngle(drag.hold !== null ? drag.hold : raw, false);
  };

  CropDialog.prototype.onRulerUp = function (event) {
    var drag = this.drag;
    if (!drag || drag.id !== event.pointerId) return;
    this.drag = null;
    if (this.angle !== drag.start) this.announce(fill(strings().rotated, { value: angleLabel(this.angle, true) }));
    this.straighten(document.activeElement === this.parts.ruler);
  };

  // A short "snapped" mark on the angle readout (no movement when reduced motion is asked for: see the CSS).
  CropDialog.prototype.pulse = function () {
    var el = this.parts.angle;
    if (!el) return;
    el.classList.remove('is-snapped');
    void el.offsetWidth;
    el.classList.add('is-snapped');
    window.clearTimeout(this.pulseTimer);
    this.pulseTimer = window.setTimeout(function () {
      el.classList.remove('is-snapped');
    }, 700);
  };

  CropDialog.prototype.onRulerKey = function (event) {
    if (!this.img || this.quarterOnly) return;
    var step = this.rule.step || 0.5;
    var handled = true;
    if (event.key === 'ArrowRight' || event.key === 'ArrowUp') this.turnBy(event.shiftKey ? this.rule.fine_step || 1 : step, false);
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') this.turnBy(-(event.shiftKey ? this.rule.fine_step || 1 : step), false);
    else if (event.key === 'PageUp') this.turnBy(10, false);
    else if (event.key === 'PageDown') this.turnBy(-10, false);
    else if (event.key === 'Home') this.setAngle(this.rule.min + step, false);
    else if (event.key === 'End') this.setAngle(this.rule.max, false);
    else handled = false;
    if (handled) event.preventDefault();
  };

  CropDialog.prototype.onRulerWheel = function (event) {
    if (!this.img || this.quarterOnly) return;
    event.preventDefault();
    var delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
    if (delta) this.turnBy((delta > 0 ? 1 : -1) * (this.rule.step || 0.5), false);
  };

  // ---- the check step: exactly what will be printed ----

  CropDialog.prototype.drawResult = function () {
    if (!this.img) return;
    var crop = this.finalCrop();
    var W = this.photo.width;
    var H = this.photo.height;
    var canvas = this.parts.result;
    var cssW = Math.min((this.parts.check.clientWidth || 320) - 8, 360);
    var dpr = Math.min(window.devicePixelRatio || 1, 3);
    canvas.style.width = cssW + 'px';
    canvas.style.height = Math.round(cssW / this.spec.aspect) + 'px';
    drawCrop(canvas, this.img, W, H, this.angle, crop, Math.round(cssW * dpr), Math.round((cssW * dpr) / this.spec.aspect));
    canvas.dataset.angle = String(this.angle);
    canvas.dataset.sentCrop = [crop.x, crop.y, crop.w, crop.h].join(',');
    var quality = qualityOf(ppiOf(crop, W, H, this.angle, this.spec), this.spec.quality_ppi);
    this.parts.resultquality.textContent = qualityText(quality);
    this.parts.resultquality.dataset.quality = quality;
    this.parts.resultsize.textContent = fill(strings().result_size, { width: this.spec.face_mm.width, height: this.spec.face_mm.height });
    if (this.parts.resultangle) {
      this.parts.resultangle.textContent = this.angle === 0 ? '' : fill(strings().result_angle, { value: angleLabel(this.angle, false) });
      this.parts.resultangle.hidden = this.angle === 0;
    }
    // The service refuses a crop with a blank corner; so does this step, rather than letting it through.
    var fits = cropFits(crop, W, H, this.angle);
    this.parts.confirm.toggleAttribute('disabled', !fits);
    this.error(fits ? '' : strings().error_crop);
  };

  CropDialog.prototype.error = function (text) {
    if (!this.parts.error) return;
    this.parts.error.textContent = text;
    this.parts.error.hidden = !text;
  };

  CropDialog.prototype.confirm = function () {
    if (this.saving || !this.field) return;
    var field = this.field;
    var crop = this.finalCrop();
    if (!cropFits(crop, this.photo.width, this.photo.height, this.angle)) {
      this.error(strings().error_crop);
      return;
    }
    this.saving = true;
    this.error('');
    var button = this.parts.confirm;
    button.setAttribute('aria-disabled', 'true');
    button.classList.add('loading');
    field
      .saveCrop(this.angle, crop)
      .then(
        () => {
          this.saving = false;
          this.close(true);
        },
        (error) => {
          this.saving = false;
          if (error && error.code === 'upload_gone') {
            this.close(false);
            field.gone();
            return;
          }
          this.error(error && error.code === 'network' ? strings().error_network : strings().error_crop);
        }
      )
      .finally(function () {
        button.removeAttribute('aria-disabled');
        button.classList.remove('loading');
      });
  };

  // ---- <cgl-photo-thumb> (cart) -------------------------------------------------------------------------

  class CglPhotoThumb extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      var units = [];
      try {
        var data = JSON.parse(this.dataset.cglData || '{}');
        units = (Array.isArray(data.units) ? data.units : []).map(function (u) {
          return photoRef(u && u.photo);
        });
      } catch (e) {
        units = [];
      }
      this.refs = units.filter(Boolean);
      var aspect = parseFloat(this.dataset.aspect) || 0;
      this.refs.slice(0, 3).forEach((ref) => this.draw(ref, aspect));
      checkPhotos(this.refs.map((r) => r.asset)).then((status) => {
        var gone = this.refs.some((r) => status[r.asset] === 'gone');
        this.refs.forEach((r) => {
          if (status[r.asset] === 'gone') forgetToken(r.asset);
        });
        this.expired(gone);
      });
    }

    disconnectedCallback() {
      guardLater();
    }

    expired(gone) {
      this.toggleAttribute('data-expired', gone);
      var note = this.parentElement && this.parentElement.querySelector('[data-cgl-photo-expired]');
      if (note) note.hidden = !gone;
      if (gone) this.hidden = true;
      guardLater();
    }

    draw(ref, aspect) {
      var saved = tokenFor(ref.asset);
      if (!saved) return;
      var service = Service.get(saved.s);
      service
        .link(ref.asset, saved.t)
        .then(function (data) {
          return loadImage(data.preview.url);
        })
        .then(
          (img) => {
            if (this.hasAttribute('data-expired')) return;
            var canvas = document.createElement('canvas');
            canvas.className = 'cgl-ph-thumb__img';
            canvas.setAttribute('role', 'img');
            canvas.setAttribute('aria-label', this.dataset.label || '');
            this.appendChild(canvas);
            var master = ref.master || { w: img.naturalWidth, h: img.naturalHeight };
            var size = cropSize(ref.crop, master.w, master.h, ref.angle);
            var a = aspect || size.width / size.height;
            var w = Math.round(64 * Math.min(window.devicePixelRatio || 1, 3));
            drawCrop(canvas, img, master.w, master.h, ref.angle, ref.crop, w, Math.round(w / a));
            this.hidden = false;
          },
          (error) => {
            if (error && error.code === 'upload_gone') this.expired(true);
          }
        );
    }
  }

  // ---- the expired-photo checkout guard ---------------------------------------------------------------------
  //
  // Phase 4B item 9 (8 Oct): a photo kept for a cart expires 7 days (RETENTION carted_hours) after its last use. When
  // a cart line's photo has gone, the line says so, the theme's checkout buttons are disabled with a plain reason
  // and the accelerated checkout buttons are hidden, until the line is edited (new photo) or removed. Only
  // buttons this script disabled are re-enabled. The order webhook still flags such an order "production
  // blocked / missing photo" for the paths a theme can't stop (docs/phase-4b-webhooks.md).

  var photoStatus = {}; // asset -> 'ok' | 'gone' for this page
  var pendingStatus = {}; // service -> { ids, waiters }

  // Resolves to { asset: 'ok' | 'gone' } for the assets it could check. Fails open: a photo the service could not
  // be asked about is not reported gone.
  function checkPhotos(assets) {
    var byService = {};
    var known = {};
    assets.forEach(function (asset) {
      if (photoStatus[asset]) {
        known[asset] = photoStatus[asset];
        return;
      }
      var saved = tokenFor(asset);
      var base = (saved && saved.s) || knownService();
      if (!base) return;
      (byService[base] = byService[base] || []).push(asset);
    });
    var waits = Object.keys(byService).map(function (base) {
      return queueStatus(base, byService[base]);
    });
    return Promise.all(waits).then(function () {
      assets.forEach(function (asset) {
        if (photoStatus[asset]) known[asset] = photoStatus[asset];
      });
      return known;
    });
  }

  // Batches the status questions of every cart line drawn in the same moment into one call per service.
  function queueStatus(base, ids) {
    var p = pendingStatus[base];
    if (!p) {
      p = pendingStatus[base] = { ids: [], promise: null };
      p.promise = new Promise(function (resolve) {
        window.setTimeout(function () {
          delete pendingStatus[base];
          var unique = p.ids.filter(function (id, i) {
            return p.ids.indexOf(id) === i;
          });
          var batches = [];
          for (var i = 0; i < unique.length; i += 20) batches.push(unique.slice(i, i + 20));
          Promise.all(
            batches.map(function (batch) {
              return Service.get(base)
                .status(batch)
                .then(
                  function (status) {
                    batch.forEach(function (id) {
                      if (status[id] === 'ok' || status[id] === 'gone') photoStatus[id] = status[id];
                    });
                  },
                  function () {
                    /* fail open: never hold checkout because the photo service could not be reached */
                  }
                );
            })
          ).then(resolve);
        }, 30);
      });
    }
    ids.forEach(function (id) {
      p.ids.push(id);
    });
    return p.promise;
  }

  var guardTimer = 0;
  var guardObserver = null;
  var cartGone = false; // set from the cart itself (product page, after Add to cart)

  function guardLater() {
    window.clearTimeout(guardTimer);
    guardTimer = window.setTimeout(guard, 0);
  }

  function blockedText() {
    var thumb = document.querySelector('cgl-photo-thumb[data-blocked-text]');
    return (thumb && thumb.dataset.blockedText) || strings().cart_blocked || '';
  }

  function guard() {
    var expired = cartGone || !!document.querySelector('cgl-photo-thumb[data-expired]');
    document.querySelectorAll('button[name="checkout"], input[name="checkout"]').forEach(function (button) {
      var ours = button.hasAttribute('data-cgl-photo-held');
      if (expired && !ours) {
        button.setAttribute('data-cgl-photo-held', button.disabled ? 'was-disabled' : '');
        button.disabled = true;
        var note = document.createElement('p');
        note.className = 'cgl-ph-cart__blocked';
        note.setAttribute('data-cgl-photo-held-note', '');
        note.setAttribute('role', 'status');
        note.textContent = blockedText();
        button.insertAdjacentElement('beforebegin', note);
      } else if (!expired && ours) {
        if (button.getAttribute('data-cgl-photo-held') !== 'was-disabled') button.disabled = false;
        button.removeAttribute('data-cgl-photo-held');
        var prev = button.previousElementSibling;
        if (prev && prev.hasAttribute('data-cgl-photo-held-note')) prev.remove();
      }
    });
    document.querySelectorAll('.cart__dynamic-checkout-buttons, .additional-checkout-buttons').forEach(function (el) {
      if (expired && !el.hasAttribute('data-cgl-photo-held')) {
        el.setAttribute('data-cgl-photo-held', el.hidden ? 'was-hidden' : '');
        el.hidden = true;
      } else if (!expired && el.hasAttribute('data-cgl-photo-held')) {
        if (el.getAttribute('data-cgl-photo-held') !== 'was-hidden') el.hidden = false;
        el.removeAttribute('data-cgl-photo-held');
      }
    });
    // The cart page and drawer re-render their HTML after a quantity change: hold the new buttons too.
    if (expired && !guardObserver && window.MutationObserver) {
      guardObserver = new MutationObserver(guardLater);
      guardObserver.observe(document.body, { childList: true, subtree: true });
    } else if (!expired && guardObserver) {
      guardObserver.disconnect();
      guardObserver = null;
    }
  }

  // A checkout started any other way from this page (Enter in the cart form) is stopped too while held.
  document.addEventListener(
    'submit',
    function (event) {
      var submitter = event.submitter;
      if (!submitter || submitter.name !== 'checkout') return;
      if (cartGone || document.querySelector('cgl-photo-thumb[data-expired]')) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true
  );

  // Product page, after Add to cart: the "Added to cart" popup has its own Check out button, so the cart's other
  // photo lines are checked too.
  function checkCart() {
    var routes = window.routes || { cart_url: '/cart' };
    return fetch(routes.cart_url + '.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        var assets = [];
        (cart.items || []).forEach(function (item) {
          var props = item.properties || {};
          if (!props._cgl_profile || !props._cgl_data) return;
          try {
            (JSON.parse(props._cgl_data).units || []).forEach(function (u) {
              var ref = photoRef(u && u.photo);
              if (ref) assets.push(ref.asset);
            });
          } catch (e) {
            /* not a photo line */
          }
        });
        return assets.length ? checkPhotos(assets) : {};
      })
      .then(function (status) {
        cartGone = Object.keys(status).some(function (a) {
          return status[a] === 'gone';
        });
        guard();
        return cartGone;
      })
      .catch(function () {
        return false;
      });
  }

  // ---- after Add to cart ------------------------------------------------------------------------------------

  // Tells the service each photo reached the cart (keeps it while the cart is active; only an order makes it
  // permanent) and remembers the token for cart thumbnails. Then checks the rest of the cart's photos.
  function carted(assets) {
    assets.forEach(function (a) {
      saveToken(a.asset, a.token, a.service, { cart: true });
      Service.get(a.service)
        .carted(a.asset, a.token)
        .catch(function () {
          /* informational only */
        });
    });
    checkCart();
  }

  window.CglPhoto = {
    version: 2,
    carted: carted,
    checkCart: checkCart,
    clearDraft: clearDraft,
    readDraft: readDraft,
    writeDraft: writeDraft,
    strings: strings,
    // The open crop dialog's state, read-only, for the browser checks (tools/browser-qa/phase-4b.js).
    dialogState: function () {
      var d = dialogInstance;
      if (!d || !d.el || !d.el.open || !d.model) return null;
      var m = d.model;
      return { angle: d.angle, zoom: m.zoom, minZoom: m.minZoom, maxZoom: m.maxZoom, raised: m.raised, centre: { x: m.x, y: m.y }, crop: d.finalCrop(), quarterOnly: !!d.quarterOnly };
    },
    // The crop maths, for tests and the cart: the same results as the upload service.
    geometry: { trig: trig, turnedBox: turnedBox, boxToMaster: boxToMaster, cropFits: cropFits, cropSize: cropSize, wrapAngle: wrapAngle, photoRef: photoRef },
  };

  if (!customElements.get('cgl-photo')) customElements.define('cgl-photo', CglPhotoField);
  if (!customElements.get('cgl-photo-thumb')) customElements.define('cgl-photo-thumb', CglPhotoThumb);
})();
